/**
 * React Patient Self-Registration Page (Issue #274)
 *
 * Faithful port of the legacy AngularJS self-registration portal
 * (TECWeb/TECAuth/templates/self_registration.html +
 *  TECWeb/TECAuth/static/js/self_registration.js).
 *
 * External, unauthenticated multi-step form:
 *   step 1  -> upload documents (multipart POST, then poll for AI extraction)
 *   step 2  -> review & edit all extracted demographics / guarantor / insurance
 *   step 3  -> triage questions (dynamic, from analysis.triage_questions)
 *   step 4  -> consent + e-signature + registration-packet document links
 *   step 99 -> success
 *   step -99 -> error
 *
 * Follows the pattern of the other standalone auth pages (LoginPage,
 * PasswordResetConfirmPage): inline styles, a getCsrfToken() helper, raw
 * fetch() (the shared apiService only sends JSON so it cannot carry the
 * multipart upload), useParams() for the facility code.
 *
 * NOTE: The legacy portal used the optional `browser-image-compression`
 * global to shrink images client-side, falling back to the original file
 * when the library was absent. To avoid adding an npm dependency we always
 * upload the original files here (the Angular no-library fallback path).
 * Client-side image compression is a deferred follow-up.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { normalizeExtractedRegistrationChoices } from './selfRegistrationChoiceUtils';
import { isMinor, toDateInputValue } from './selfRegistrationDateUtils';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

// The extracted registration payload is a loose bag of string/boolean fields
// plus a triage_questions array and a registration_packet map. It is edited in
// place across steps 2-4 and posted back verbatim, so an index signature is the
// most faithful representation.
interface TriageQuestion {
  question: string;
  choices: string[];
  answer: string;
}

interface PacketDoc {
  name: string;
  url: string;
}

interface Analysis {
  [key: string]: any;
  triage_questions?: TriageQuestion[];
  registration_packet?: Record<string, PacketDoc> | PacketDoc[];
}

interface Step1Form {
  dl_front_image: File | null;
  insurance_front_image: File | null;
  insurance_back_image: File | null;
  is_patient: boolean;
  is_responsible_party: boolean;
  symptoms_description: string;
  text_consent: boolean;
}

interface ValidationRule {
  required: true | 'conditional';
  condition?: 'notSelfPay' | 'needsGuarantor';
  message: string;
}

interface FieldValidation {
  valid: boolean;
  message?: string;
}

interface ValidationSummary {
  valid: boolean;
  errors: { field: string; message: string }[];
  completionRate: number;
  requiredFieldCount: number;
  completedFieldCount: number;
}

// ---------------------------------------------------------------------------
// ValidationService (ported verbatim from ValidationService in
// self_registration.js)
// ---------------------------------------------------------------------------

const REQUIRED_FIELDS: Record<string, ValidationRule> = {
  // Always required fields
  patient_first_name: { required: true, message: 'Patient first name is required' },
  patient_last_name: { required: true, message: 'Patient last name is required' },
  patient_date_of_birth: { required: true, message: 'Patient date of birth is required' },
  patient_gender: { required: true, message: 'Patient gender is required' },
  patient_home_address_line_1: { required: true, message: 'Patient address is required' },
  patient_home_address_city: { required: true, message: 'Patient city is required' },
  patient_home_address_state: { required: true, message: 'Patient state is required' },
  patient_home_address_zip: { required: true, message: 'Patient ZIP code is required' },
  patient_phone: { required: true, message: 'Patient phone number is required' },

  // Conditionally required - insurance fields (unless self-pay)
  insurance_company: { required: 'conditional', condition: 'notSelfPay', message: 'Insurance company is required' },
  insurance_policy_number: { required: 'conditional', condition: 'notSelfPay', message: 'Insurance member ID is required' },
  insurance_holder_name: { required: 'conditional', condition: 'notSelfPay', message: 'Insurance holder name is required' },
  insurance_holder_dob: { required: 'conditional', condition: 'notSelfPay', message: 'Insurance holder date of birth is required' },

  // Conditionally required - guarantor fields (for minors or when not self)
  guarantor_first_name: { required: 'conditional', condition: 'needsGuarantor', message: 'Guarantor first name is required' },
  guarantor_last_name: { required: 'conditional', condition: 'needsGuarantor', message: 'Guarantor last name is required' },
  guarantor_address_line_1: { required: 'conditional', condition: 'needsGuarantor', message: 'Guarantor address is required' },
  guarantor_phone: { required: 'conditional', condition: 'needsGuarantor', message: 'Guarantor phone number is required' },
};


function isFieldRequired(fieldName: string, formData: Analysis): boolean {
  const rule = REQUIRED_FIELDS[fieldName];
  if (!rule) return false;
  if (rule.required === true) return true;
  if (rule.required === 'conditional' && rule.condition) {
    switch (rule.condition) {
      case 'notSelfPay':
        return !formData.no_insurance;
      case 'needsGuarantor':
        return (
          isMinor(formData.patient_date_of_birth) ||
          (!!formData.guarantor_patient_relationship &&
            formData.guarantor_patient_relationship !== 'ONESELF')
        );
      default:
        return false;
    }
  }
  return false;
}

function validateField(fieldName: string, value: any, formData: Analysis): FieldValidation {
  const required = isFieldRequired(fieldName, formData);
  const rule = REQUIRED_FIELDS[fieldName];

  if (required && (value === undefined || value === null || value.toString().trim() === '')) {
    return { valid: false, message: rule ? rule.message : fieldName + ' is required' };
  }

  if (fieldName === 'patient_phone' && value) {
    const phoneRegex = /^\d{10}$/;
    if (!phoneRegex.test(value.toString().replace(/\D/g, ''))) {
      return { valid: false, message: 'Please enter a valid 10-digit phone number' };
    }
  }

  if (fieldName === 'patient_home_address_zip' && value) {
    const zipRegex = /^\d{5}(-\d{4})?$/;
    if (!zipRegex.test(value.toString().trim())) {
      return { valid: false, message: 'Please enter a valid ZIP code' };
    }
  }

  return { valid: true };
}

function validateForm(formData: Analysis): ValidationSummary {
  const errors: { field: string; message: string }[] = [];
  let requiredFieldCount = 0;
  let completedFieldCount = 0;

  for (const fieldName in REQUIRED_FIELDS) {
    if (isFieldRequired(fieldName, formData)) {
      requiredFieldCount++;
      const validation = validateField(fieldName, formData[fieldName], formData);
      if (!validation.valid) {
        errors.push({ field: fieldName, message: validation.message! });
      } else {
        completedFieldCount++;
      }
    }
  }

  return {
    valid: errors.length === 0,
    errors,
    completionRate: requiredFieldCount > 0 ? (completedFieldCount / requiredFieldCount) * 100 : 100,
    requiredFieldCount,
    completedFieldCount,
  };
}

// Convert field names to user-friendly section/field labels
const FIELD_DISPLAY_NAMES: Record<string, string> = {
  // Patient Information Section
  patient_first_name: 'Patient Info / First Name',
  patient_last_name: 'Patient Info / Last Name',
  patient_date_of_birth: 'Patient Info / Date of Birth',
  patient_gender: 'Patient Info / Gender',
  patient_home_address_line_1: 'Patient Info / Address',
  patient_home_address_city: 'Patient Info / City',
  patient_home_address_state: 'Patient Info / State',
  patient_home_address_zip: 'Patient Info / ZIP Code',
  patient_phone: 'Patient Info / Phone Number',

  // Insurance Section
  insurance_company: 'Insurance / Company',
  insurance_policy_number: 'Insurance / Member ID',
  insurance_holder_name: 'Insurance / Holder Name',
  insurance_holder_dob: 'Insurance / Holder Date of Birth',

  // Guarantor Section
  guarantor_first_name: 'Guarantor / First Name',
  guarantor_last_name: 'Guarantor / Last Name',
  guarantor_address_line_1: 'Guarantor / Address',
  guarantor_phone: 'Guarantor / Phone Number',
};

function getFieldDisplayName(fieldName: string): string {
  return FIELD_DISPLAY_NAMES[fieldName] || fieldName;
}

// ---------------------------------------------------------------------------
// Shared option lists (ported verbatim from the Angular template)
// ---------------------------------------------------------------------------

const US_STATES = [
  'AK', 'AL', 'AR', 'AZ', 'CA', 'CO', 'CT', 'DC', 'DE', 'FL', 'GA', 'HI', 'IA',
  'ID', 'IL', 'IN', 'KS', 'KY', 'LA', 'MA', 'MD', 'ME', 'MI', 'MN', 'MO', 'MS',
  'MT', 'NC', 'ND', 'NE', 'NH', 'NJ', 'NM', 'NV', 'NY', 'OH', 'OK', 'OR', 'PA',
  'RI', 'SC', 'SD', 'TN', 'TX', 'UT', 'VA', 'VT', 'WA', 'WI', 'WV', 'WY',
];

const GENDER_OPTIONS = [
  { value: 'M', label: 'Male' },
  { value: 'F', label: 'Female' },
  { value: 'UNK', label: 'Unknown' },
];

const GENDER_IDENTITY_OPTIONS = [
  { value: '446151000124109', label: 'Identifies as male' },
  { value: '446141000124107', label: 'Identifies as female' },
  { value: '407377005', label: 'Female-to-Male (FTM)/Transgender Male' },
  { value: '407376001', label: 'Male-to-Female (MTF)/Transgender Female' },
  { value: '446131000124102', label: 'Genderqueer, neither exclusively male nor female' },
  { value: 'OTH', label: 'Other' },
  { value: 'ASKU', label: 'Choose not to disclose' },
];

const SEXUAL_ORIENTATION_OPTIONS = [
  { value: '20430005', label: 'Straight or heterosexual' },
  { value: '38628009', label: 'Lesbian, gay or homosexual' },
  { value: '42035005', label: 'Bisexual' },
  { value: 'OTH', label: 'Something else' },
  { value: 'UNK', label: 'Do not know' },
  { value: 'ASKU', label: 'Choose not to disclose' },
];

const MARITAL_STATUS_OPTIONS = [
  { value: 'C', label: 'Child' },
  { value: 'D', label: 'Divorced' },
  { value: 'M', label: 'Married' },
  { value: 'S', label: 'Single' },
  { value: 'W', label: 'Widowed' },
  { value: 'U', label: 'Unknown' },
];

const RACE_OPTIONS = [
  { value: '2106-3', label: 'White' },
  { value: '2054-5', label: 'Black / African American' },
  { value: '2028-9', label: 'Asian' },
  { value: '1002-5', label: 'Native American / Alaskan Natives' },
  { value: '2076-8', label: 'Native Hawaiians / Other Pacific Islanders' },
  { value: '2118-8', label: 'Middle Eastern / North African' },
  { value: 'MORE', label: 'Two or more races' },
  { value: '2131-1', label: 'Other' },
];

const ETHNICITY_OPTIONS = [
  { value: '2135-2', label: 'Hispanic or Latino' },
  { value: '2186-5', label: 'Not Hispanic or Latino' },
];

const RELIGION_OPTIONS = [
  { value: '1001', label: 'Adventist' },
  { value: '1007', label: 'Atheist' },
  { value: '1004', label: 'Agnostic' },
  { value: '1009', label: 'Baptist' },
  { value: '1029', label: 'Buddhism (Mahayana)' },
  { value: '1051', label: 'Buddhism (Theravada)' },
  { value: '1041', label: 'Catholic' },
  { value: '1013', label: 'Christianity' },
  { value: '1069', label: 'Episcopalian' },
  { value: '1020', label: 'Hinduism' },
  { value: '1023', label: 'Islam' },
  { value: '1025', label: 'Jehovah Witness' },
  { value: '1026', label: 'Judaism' },
  { value: '1073', label: 'Methodist' },
  { value: '1027', label: 'Mormon' },
  { value: '1036', label: 'Orthodox' },
  { value: '1038', label: 'Pentecostal' },
  { value: '1076', label: 'Presbyterian' },
  { value: '1077', label: 'Protestant' },
  { value: '1050', label: 'Taoism' },
  { value: 'OTH', label: 'Other' },
  { value: 'ASKU', label: 'Choose not to disclose' },
];

const LANGUAGE_OPTIONS = [
  { value: 'en', label: 'English' },
  { value: 'es', label: 'Spanish' },
  { value: 'zh', label: 'Chinese' },
  { value: 'hi', label: 'Hindi' },
  { value: 'fr', label: 'French' },
  { value: 'de', label: 'German' },
  { value: 'ar', label: 'Arabic' },
  { value: '', label: 'Declined to specify' },
];

const SMOKING_STATUS_OPTIONS = [
  { value: '449868002', label: 'Current everyday smoker' },
  { value: '428041000124106', label: 'Current some day smoker' },
  { value: '8517006', label: 'Former smoker' },
  { value: '266919005', label: 'Never smoker' },
  { value: '77176002', label: 'Smoker, current status unknown' },
  { value: '266927001', label: 'Unknown if ever smoked' },
  { value: '428071000124103', label: 'Heavy tobacco smoker' },
  { value: '428061000124105', label: 'Light tobacco smoker' },
];

const EXTRACTED_PATIENT_CHOICE_OPTIONS = {
  patient_gender: GENDER_OPTIONS,
  patient_gender_identity: GENDER_IDENTITY_OPTIONS,
  patient_sexual_orientation: SEXUAL_ORIENTATION_OPTIONS,
  patient_martial_status: MARITAL_STATUS_OPTIONS,
  patient_smoking_status: SMOKING_STATUS_OPTIONS,
  patient_race: RACE_OPTIONS,
  ethnicity: ETHNICITY_OPTIONS,
  religion: RELIGION_OPTIONS,
  preferred_language: LANGUAGE_OPTIONS,
};

const EXTRACTED_PATIENT_CHOICE_ALIASES = {
  patient_sexual_orientation: {
    other: 'OTH',
  },
};

// Guarantor relationship options
const GUARANTOR_RELATIONSHIP_OPTIONS = [
  { value: 'ONESELF', label: 'Self' },
  { value: 'SPS', label: 'Spouse' },
  { value: 'SIB', label: 'Sibling' },
  { value: 'CHILD', label: 'Child' },
  { value: 'STPCHLD', label: 'Stepchild' },
  { value: 'CHLDFOST', label: 'Foster Child' },
  { value: 'CHLDWARD', label: 'Ward Of Court' },
  { value: 'EMP', label: 'Employee' },
  { value: 'UNK', label: 'Unknown' },
  { value: 'HDEP', label: 'Handicapped Dependent' },
  { value: 'GRNDCHILD', label: 'Grandchild' },
  { value: 'NIENEPH', label: 'Niece/Nephew' },
  { value: 'INJPLNTF', label: 'Injured Plaintiff' },
  { value: 'SPNSRDDEP', label: 'Sponsored Dependent' },
  { value: 'MNRMNRDP', label: 'Minor Dependent Of A Minor Dependent' },
  { value: 'PRN', label: 'Parent' },
  { value: 'GRPRN', label: 'Grandparent' },
  { value: 'SIGOTHR', label: 'Significant Other' },
  { value: 'OTHR', label: 'Other' },
];

// Insurance holder relationship options (superset of guarantor with NCHILD)
const INSURANCE_RELATIONSHIP_OPTIONS = [
  { value: 'ONESELF', label: 'Self' },
  { value: 'SPS', label: 'Spouse' },
  { value: 'SIB', label: 'Sibling' },
  { value: 'CHILD', label: 'Child' },
  { value: 'NCHILD', label: 'Natural Child / Insured Does Not Have Financial Responsibility' },
  { value: 'STPCHLD', label: 'Stepchild' },
  { value: 'CHLDFOST', label: 'Foster Child' },
  { value: 'CHLDWARD', label: 'Ward Of Court' },
  { value: 'EMP', label: 'Employee' },
  { value: 'UNK', label: 'Unknown' },
  { value: 'HDEP', label: 'Handicapped Dependent' },
  { value: 'GRNDCHILD', label: 'Grandchild' },
  { value: 'NIENEPH', label: 'Niece/Nephew' },
  { value: 'INJPLNTF', label: 'Injured Plaintiff' },
  { value: 'SPNSRDDEP', label: 'Sponsored Dependent' },
  { value: 'MNRMNRDP', label: 'Minor Dependent Of A Minor Dependent' },
  { value: 'PRN', label: 'Parent' },
  { value: 'GRPRN', label: 'Grandparent' },
  { value: 'SIGOTHR', label: 'Significant Other' },
  { value: 'OTHR', label: 'Other' },
];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

// CSRF helper (copied from PasswordResetConfirmPage / the LabUpload pattern).
function getCsrfToken(): string {
  const cookie = document.cookie
    .split('; ')
    .find((row) => row.startsWith('csrftoken='));
  return cookie ? cookie.split('=')[1] : '';
}


// ---------------------------------------------------------------------------
// Inline styles (mirrors the LabUpload/auth inline-style approach)
// ---------------------------------------------------------------------------

const styles: Record<string, React.CSSProperties> = {
  page: {
    maxWidth: 640,
    margin: '0 auto',
    padding: '0 16px 120px',
    fontFamily: 'Arial, Helvetica, sans-serif',
    color: '#222',
  },
  header: {
    textAlign: 'center',
    padding: '24px 0',
  },
  logo: {
    maxWidth: 220,
    height: 'auto',
  },
  field: {
    display: 'flex',
    flexDirection: 'column',
    marginBottom: 16,
  },
  label: {
    fontWeight: 600,
    marginBottom: 4,
    fontSize: 14,
  },
  input: {
    padding: '8px 10px',
    fontSize: 15,
    border: '1px solid #bbb',
    borderRadius: 4,
  },
  button: {
    background: '#111',
    color: '#fff',
    border: 'none',
    borderRadius: 4,
    padding: '12px 20px',
    fontSize: 16,
    cursor: 'pointer',
    marginTop: 8,
  },
  errorText: {
    color: '#c0392b',
    fontSize: 13,
    marginTop: 4,
  },
  requiredLabel: {
    color: '#111',
  },
  banner: {
    background: '#fdecea',
    border: '1px solid #f5c6cb',
    color: '#a83232',
    padding: '10px 14px',
    borderRadius: 4,
    marginBottom: 16,
    whiteSpace: 'pre-wrap',
  },
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    background: 'rgba(0,0,0,0.8)',
    zIndex: 10000,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'white',
    fontSize: '1.2em',
  },
  spinner: {
    border: '4px solid rgba(255,255,255,0.3)',
    borderTop: '4px solid white',
    borderRadius: '50%',
    width: 40,
    height: 40,
    animation: 'sr-spin 1s linear infinite',
  },
  progressWrap: {
    background: '#f4f6f8',
    border: '1px solid #dfe3e6',
    borderRadius: 6,
    padding: 12,
    marginBottom: 20,
  },
  progressBar: {
    background: '#dfe3e6',
    borderRadius: 4,
    height: 10,
    overflow: 'hidden',
    margin: '8px 0',
  },
  progressFill: {
    background: '#2e7d32',
    height: '100%',
    transition: 'width 0.3s ease',
  },
  minorIndicator: {
    color: '#8a6d3b',
    background: '#fcf8e3',
    border: '1px solid #faebcc',
    borderRadius: 4,
    padding: '6px 10px',
    marginTop: 6,
    fontSize: 13,
  },
};

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

const Header: React.FC = () => (
  <div style={styles.header}>
    <img id="auth-logo" style={styles.logo} src="/static/imgs/logo_black.png" alt="TEC" />
  </div>
);

const LoadingOverlay: React.FC<{ message: string }> = ({ message }) => (
  <div style={styles.overlay}>
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <style>{'@keyframes sr-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }'}</style>
      <div style={styles.spinner} />
      <div style={{ marginTop: 16, textAlign: 'center' }}>{message || 'Processing documents...'}</div>
    </div>
  </div>
);

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

const POLL_INTERVAL_MS = 2000;
const POLL_RETRY_MS = 3000;
// How many consecutive "not_found" polls to tolerate before treating it as a real failure.
// Covers the brief window where the status cache lags just after upload (~a few seconds).
const MAX_NOT_FOUND_POLLS = 5;
const OCR_IMAGE_ACCEPT = 'image/jpeg,image/png,.jpg,.jpeg,.png';

const getUnsupportedOcrImageMessage = (file: File | null): string => {
  if (!file) return '';
  const extension = file.name.toLowerCase().match(/\.[^.]+$/)?.[0] || '';
  const supportedExtension = ['.jpg', '.jpeg', '.png'].includes(extension);
  const supportedMimeType = !file.type || ['image/jpeg', 'image/jpg', 'image/png'].includes(file.type.toLowerCase());
  if (supportedExtension && supportedMimeType) return '';
  return `${file.name} cannot be read. Please upload a JPEG or PNG image; HEIC and HEIF images are not supported.`;
};

const SelfRegistrationPage: React.FC = () => {
  const { facilityCode } = useParams<{ facilityCode?: string }>();

  const [step, setStep] = useState<number>(1);
  const [loading, setLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('');
  const [errorBanner, setErrorBanner] = useState('');

  const [form, setForm] = useState<Step1Form>({
    dl_front_image: null,
    insurance_front_image: null,
    insurance_back_image: null,
    is_patient: false,
    is_responsible_party: false,
    symptoms_description: '',
    text_consent: false,
  });

  const [analysis, setAnalysis] = useState<Analysis | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, FieldValidation>>({});

  // Track mount + the poll retry-once state (mirrors the Angular "retry once
  // more, then give up" behavior on a failed poll request).
  const mountedRef = useRef(true);
  const pollRetriedRef = useRef(false);
  const notFoundPollsRef = useRef(0);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Live validation summary (Angular recomputed this via $watch on analysis).
  const validationSummary: ValidationSummary = useMemo(
    () => (analysis ? validateForm(analysis) : {
      valid: false, errors: [], completionRate: 0, requiredFieldCount: 0, completedFieldCount: 0,
    }),
    [analysis]
  );

  // ---- field helpers -------------------------------------------------------

  const setField = (name: string, value: any) => {
    setAnalysis((prev) => (prev ? { ...prev, [name]: value } : prev));
    setFieldErrors((previousErrors) => {
      if (!(name in previousErrors)) return previousErrors;
      const nextErrors = { ...previousErrors };
      delete nextErrors[name];
      return nextErrors;
    });
  };

  const runFieldValidation = (fieldName: string) => {
    setAnalysis((prev) => {
      if (!prev) return prev;
      const validation = validateField(fieldName, prev[fieldName], prev);
      setFieldErrors((fe) => ({ ...fe, [fieldName]: validation }));
      return prev;
    });
  };

  const hasFieldError = (fieldName: string) =>
    !!fieldErrors[fieldName] && !fieldErrors[fieldName].valid;

  const getFieldError = (fieldName: string) =>
    fieldErrors[fieldName] ? fieldErrors[fieldName].message || '' : '';

  const setDocumentFile = (
    fieldName: 'dl_front_image' | 'insurance_front_image' | 'insurance_back_image',
    file: File | null,
    input: HTMLInputElement,
  ) => {
    const formatError = getUnsupportedOcrImageMessage(file);
    if (formatError) {
      input.value = '';
      setForm((previous) => ({ ...previous, [fieldName]: null }));
      setErrorBanner(formatError);
      return;
    }
    setForm((previous) => ({ ...previous, [fieldName]: file }));
    setErrorBanner('');
  };

  // ---- ONESELF copiers (mirror the two Angular $watch handlers) ------------

  // When "Self" is chosen as the guarantor relationship, copy patient
  // personal/address/contact fields into the guarantor fields. Writes
  // `guarantor_gender` — the field the backend reads and the field the step-2
  // <select> is now (correctly) bound to.
  useEffect(() => {
    if (analysis?.guarantor_patient_relationship !== 'ONESELF') return;
    setAnalysis((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        guarantor_first_name: prev.patient_first_name,
        guarantor_middle_name: prev.patient_middle_name,
        guarantor_last_name: prev.patient_last_name,
        guarantor_date_of_birth: prev.patient_date_of_birth,
        guarantor_gender: prev.patient_gender,
        guarantor_ssn: prev.patient_ssn,
        guarantor_address_line_1: prev.patient_home_address_line_1,
        guarantor_address_line_2: prev.patient_home_address_line_2,
        guarantor_address_city: prev.patient_home_address_city,
        guarantor_address_state: prev.patient_home_address_state,
        guarantor_address_zip: prev.patient_home_address_zip,
        guarantor_phone: prev.patient_phone,
        guarantor_employer_name: prev.patient_employer_name,
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [analysis?.guarantor_patient_relationship]);

  // When "Self" is chosen as the insurance holder relationship, copy patient
  // name/dob/gender/phone into the insurance_holder fields. Bug fix vs. legacy:
  // the AngularJS copier wrote `insurance_holder_date_of_birth`, but the backend
  // (process_self_registration_two, views.py:675) reads `insurance_holder_dob` —
  // so Self-pay holder DOB was silently dropped. Write the field the backend reads.
  useEffect(() => {
    if (analysis?.insurance_patient_relationship !== 'ONESELF') return;
    setAnalysis((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        insurance_holder_name: `${prev.patient_first_name || ''} ${prev.patient_last_name || ''}`,
        insurance_holder_dob: prev.patient_date_of_birth,
        insurance_holder_gender: prev.patient_gender,
        insurance_holder_phone: prev.patient_phone,
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [analysis?.insurance_patient_relationship]);

  // Revalidate dependent fields when the conditions that gate them change
  // (mirrors the Angular $watch on patient_date_of_birth and no_insurance).
  useEffect(() => {
    if (!analysis) return;
    ['guarantor_first_name', 'guarantor_last_name', 'guarantor_address_line_1', 'guarantor_phone'].forEach(
      runFieldValidation
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [analysis?.patient_date_of_birth]);

  useEffect(() => {
    if (!analysis) return;
    ['insurance_company', 'insurance_policy_number', 'insurance_holder_name', 'insurance_holder_dob'].forEach(
      runFieldValidation
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [analysis?.no_insurance]);

  // ---- step 1 submit + polling --------------------------------------------

  // Populate inline "required" errors for every currently-invalid required field so the
  // reviewer sees, on open, exactly what still needs filling — rather than only after
  // focusing and blurring each field. Mirrors the field-level messages runFieldValidation
  // sets on blur, but computed up front from the freshly extracted data.
  const showAllRequiredErrors = (data: Analysis) => {
    const validation = validateForm(data);
    const errs: Record<string, FieldValidation> = {};
    validation.errors.forEach((err) => {
      errs[err.field] = { valid: false, message: err.message };
    });
    setFieldErrors(errs);
  };

  const pollForCompletion = (processingId: string) => {
    fetch(`/auth/check_self_registration_status/${processingId}`, {
      method: 'GET',
      credentials: 'same-origin',
    })
      .then((res) => res.json())
      .then((data) => {
        if (!mountedRef.current) return;
        const status = data.status;

        if (status === 'completed') {
          pollRetriedRef.current = false;
          const result = normalizeExtractedRegistrationChoices<Analysis>(
            data.result || {},
            EXTRACTED_PATIENT_CHOICE_OPTIONS,
            EXTRACTED_PATIENT_CHOICE_ALIASES,
          );
          // Convert ISO date strings to YYYY-MM-DD for <input type="date">.
          if (result.patient_date_of_birth)
            result.patient_date_of_birth = toDateInputValue(result.patient_date_of_birth);
          if (result.guarantor_date_of_birth)
            result.guarantor_date_of_birth = toDateInputValue(result.guarantor_date_of_birth);
          if (result.insurance_holder_dob)
            result.insurance_holder_dob = toDateInputValue(result.insurance_holder_dob);

          setAnalysis(result);
          showAllRequiredErrors(result);
          setStep(2);
          setLoading(false);
          setLoadingMessage('');
        } else if (status === 'error') {
          pollRetriedRef.current = false;
          setLoading(false);
          setLoadingMessage('');
          setErrorBanner('Failed to process documents: ' + (data.message || 'Unknown error'));
        } else if (status === 'processing') {
          pollRetriedRef.current = false;
          notFoundPollsRef.current = 0;
          setLoadingMessage(data.message || 'Processing documents...');
          window.setTimeout(() => pollForCompletion(processingId), POLL_INTERVAL_MS);
        } else if (status === 'not_found' && notFoundPollsRef.current < MAX_NOT_FOUND_POLLS) {
          // The status cache can briefly lag right after upload (the async worker seeds
          // it a beat after .delay() returns). Tolerate a short "not_found" window and
          // keep polling rather than hard-failing on the first tick.
          pollRetriedRef.current = false;
          notFoundPollsRef.current += 1;
          setLoadingMessage('Processing documents...');
          window.setTimeout(() => pollForCompletion(processingId), POLL_INTERVAL_MS);
        } else {
          // Unknown status, or not_found persisted past the tolerance window.
          pollRetriedRef.current = false;
          notFoundPollsRef.current = 0;
          setLoading(false);
          setLoadingMessage('');
          setErrorBanner('Processing status unknown. Please try again.');
        }
      })
      .catch(() => {
        if (!mountedRef.current) return;
        // Polling request failed - retry once more, then give up.
        if (pollRetriedRef.current) {
          pollRetriedRef.current = false;
          setLoading(false);
          setLoadingMessage('');
          setErrorBanner('Failed to check processing status. Please try again.');
          return;
        }
        pollRetriedRef.current = true;
        window.setTimeout(() => pollForCompletion(processingId), POLL_RETRY_MS);
      });
  };

  const submitStep1 = (e: React.FormEvent) => {
    e.preventDefault();
    setErrorBanner('');

    const unsupportedImage = [
      form.dl_front_image,
      form.insurance_front_image,
      form.insurance_back_image,
    ].map(getUnsupportedOcrImageMessage).find(Boolean);
    if (unsupportedImage) {
      setErrorBanner(unsupportedImage);
      return;
    }

    pollRetriedRef.current = false;
    notFoundPollsRef.current = 0;
    setLoading(true);
    setLoadingMessage('Uploading documents...');

    const fd = new FormData();
    // Backend treats a missing/'undefined' file as None, so mirror the Angular
    // append behavior: send the File when present, else the string 'undefined'.
    fd.append('dl_front_image', form.dl_front_image ?? 'undefined');
    fd.append('insurance_front_image', form.insurance_front_image ?? 'undefined');
    fd.append('insurance_back_image', form.insurance_back_image ?? 'undefined');
    fd.append('is_patient', String(form.is_patient));
    fd.append('is_responsible_party', String(form.is_responsible_party));
    fd.append('symptoms_description', form.symptoms_description);
    fd.append('facility_code', facilityCode || '');
    fd.append('text_consent', String(form.text_consent));

    // Multipart: do NOT set Content-Type; the browser sets the boundary.
    fetch('/auth/process_self_registration_one', {
      method: 'POST',
      credentials: 'same-origin',
      body: fd,
    })
      .then(async (res) => {
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.message || 'Failed to upload documents. Please try again.');
        return data;
      })
      .then((data) => {
        if (!mountedRef.current) return;
        setLoadingMessage('Processing documents and extracting information...');
        pollForCompletion(data.processing_id);
      })
      .catch((error: unknown) => {
        if (!mountedRef.current) return;
        setLoading(false);
        setLoadingMessage('');
        setErrorBanner(error instanceof Error ? error.message : 'Failed to upload documents. Please try again.');
      });
  };

  // ---- step navigation with validation gate --------------------------------

  const nextStep = () => {
    if (!analysis) {
      setStep((s) => s + 1);
      return;
    }
    const validation = validateForm(analysis);
    if (!validation.valid) {
      const newErrors: Record<string, FieldValidation> = {};
      validation.errors.forEach((err) => {
        newErrors[err.field] = { valid: false, message: err.message };
      });
      setFieldErrors((fe) => ({ ...fe, ...newErrors }));

      const missingFieldsList = validation.errors
        .map((err) => getFieldDisplayName(err.field))
        .join(', ');
      setErrorBanner(
        'Please complete all required fields before proceeding. ' +
          validation.completedFieldCount +
          ' of ' +
          validation.requiredFieldCount +
          ' required fields completed.\n\nMissing fields: ' +
          missingFieldsList
      );
      // Scroll to first error field
      const firstErrorField = validation.errors[0];
      if (firstErrorField) {
        const el = document.querySelector(`[data-field="${firstErrorField.field}"]`) as HTMLElement | null;
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          el.focus();
        }
      }
      return;
    }
    setErrorBanner('');
    setStep((s) => s + 1);
  };

  // ---- finish --------------------------------------------------------------

  const finishRegistration = () => {
    if (!analysis) return;

    if (analysis.consent_confirmation && analysis.consent_signature !== '' && analysis.consent_signature) {
      setLoading(true);
      setLoadingMessage('Submitting registration...');
      fetch('/auth/process_self_registration_two', {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': getCsrfToken(),
        },
        body: JSON.stringify(analysis),
      })
        .then(async (res) => {
          const responseData = await res.json().catch(() => ({})) as {
            message?: string;
            field_errors?: Record<string, string | string[]>;
          };
          if (!mountedRef.current) return;
          setLoading(false);
          setLoadingMessage('');
          if (res.ok) {
            setStep(99);
          } else if (responseData.field_errors && Object.keys(responseData.field_errors).length > 0) {
            const serverFieldErrors = Object.fromEntries(
              Object.entries(responseData.field_errors).map(([fieldName, messages]) => [
                fieldName,
                {
                  valid: false,
                  message: Array.isArray(messages) ? messages.join(' ') : messages,
                },
              ]),
            );
            const firstErrorField = Object.keys(serverFieldErrors)[0];
            setFieldErrors((previousErrors) => ({ ...previousErrors, ...serverFieldErrors }));
            setErrorBanner(
              responseData.message || 'Please correct the highlighted registration fields and submit again.',
            );
            setStep(2);
            window.setTimeout(() => {
              const element = document.querySelector(`[data-field="${firstErrorField}"]`) as HTMLElement | null;
              element?.scrollIntoView({ behavior: 'smooth', block: 'center' });
              element?.focus();
            }, 0);
          } else {
            setStep(-99);
          }
        })
        .catch(() => {
          if (!mountedRef.current) return;
          setLoading(false);
          setLoadingMessage('');
          setStep(-99);
        });
    } else {
      setErrorBanner('You must consent to the statements and e-sign to proceed');
    }
  };

  // ---- render helpers ------------------------------------------------------

  const textProps = (name: string) => ({
    value: (analysis?.[name] ?? '') as string,
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => setField(name, e.target.value),
    style: styles.input,
  });

  const selectProps = (name: string) => ({
    value: (analysis?.[name] ?? '') as string,
    onChange: (e: React.ChangeEvent<HTMLSelectElement>) => setField(name, e.target.value),
    style: styles.input,
  });

  const renderRequiredError = (name: string) =>
    hasFieldError(name) ? <span style={styles.errorText}>{getFieldError(name)}</span> : null;

  // Build registration packet links (Angular built anchor HTML from
  // analysis.registration_packet). registration_packet may be an object map or
  // an array; iterate its values either way.
  const packetDocs: PacketDoc[] = useMemo(() => {
    const rp = analysis?.registration_packet;
    if (!rp) return [];
    return Object.values(rp) as PacketDoc[];
  }, [analysis]);

  // =========================================================================
  // Render
  // =========================================================================

  // --- step 99: success ---
  if (step === 99) {
    return (
      <div style={styles.page}>
        <Header />
        <h2>Thank you for registering</h2>
        <p>Please report to the receptionist to complete your check in.</p>
      </div>
    );
  }

  // --- step -99: error ---
  if (step === -99) {
    return (
      <div style={styles.page}>
        <Header />
        <h2>Unable to complete self registration</h2>
        <p>An error has occurred. Please report to the receptionist to complete your registration manually.</p>
      </div>
    );
  }

  // --- step 1: upload documents ---
  if (step === 1) {
    return (
      <div style={styles.page}>
        {loading && <LoadingOverlay message={loadingMessage} />}
        <Header />
        {errorBanner && <div style={styles.banner}>{errorBanner}</div>}
        <form onSubmit={submitStep1} encType="multipart/form-data" noValidate>
          <h2>Step 1: Upload Your Documents</h2>
          <p>Upload JPEG or PNG images. HEIC and HEIF images cannot be processed.</p>

          <div style={styles.field}>
            <label htmlFor="dl_front_image" style={styles.label}>
              Driver's License (Front)
            </label>
            <input
              id="dl_front_image"
              type="file"
              accept={OCR_IMAGE_ACCEPT}
              onChange={(e) => setDocumentFile('dl_front_image', e.currentTarget.files?.[0] ?? null, e.currentTarget)}
            />
          </div>

          <div style={styles.field}>
            <label htmlFor="insurance_front_image" style={styles.label}>
              Insurance Card (Front)
            </label>
            <input
              id="insurance_front_image"
              type="file"
              accept={OCR_IMAGE_ACCEPT}
              onChange={(e) => setDocumentFile('insurance_front_image', e.currentTarget.files?.[0] ?? null, e.currentTarget)}
            />
          </div>

          <div style={styles.field}>
            <label htmlFor="insurance_back_image" style={styles.label}>
              Insurance Card (Back)
            </label>
            <input
              id="insurance_back_image"
              type="file"
              accept={OCR_IMAGE_ACCEPT}
              onChange={(e) => setDocumentFile('insurance_back_image', e.currentTarget.files?.[0] ?? null, e.currentTarget)}
            />
          </div>

          <div style={styles.field}>
            <span style={styles.label}>I am the…</span>
            <label style={{ display: 'block', marginBottom: '0.5em' }}>
              <input
                type="checkbox"
                checked={form.is_patient}
                onChange={(e) => setForm((f) => ({ ...f, is_patient: e.target.checked }))}
              />{' '}
              Patient
            </label>
            <label style={{ display: 'block', marginBottom: '0.5em' }}>
              <input
                type="checkbox"
                checked={form.is_responsible_party}
                onChange={(e) => setForm((f) => ({ ...f, is_responsible_party: e.target.checked }))}
              />{' '}
              Responsible Party / Guarantor
            </label>
          </div>

          <div style={styles.field}>
            <label htmlFor="symptoms_description" style={styles.label}>
              Why are you here today?
            </label>
            <textarea
              id="symptoms_description"
              rows={4}
              required
              placeholder="Briefly describe your symptoms…"
              value={form.symptoms_description}
              onChange={(e) => setForm((f) => ({ ...f, symptoms_description: e.target.value }))}
              style={styles.input}
            />
          </div>

          <div style={styles.field}>
            <span style={styles.label}>Text Consent</span>
            <label style={{ display: 'block', marginBottom: '0.5em' }}>
              <input
                type="checkbox"
                checked={form.text_consent}
                onChange={(e) => setForm((f) => ({ ...f, text_consent: e.target.checked }))}
              />{' '}
              I consent to receiving text messages about my registration
            </label>
          </div>

          <button type="submit" style={styles.button}>
            Continue →
          </button>
          <div style={{ height: 100 }} />
        </form>
      </div>
    );
  }

  // --- step 3: triage questions ---
  if (step === 3) {
    const triageQuestions = analysis?.triage_questions || [];
    return (
      <div style={styles.page}>
        {loading && <LoadingOverlay message={loadingMessage} />}
        <Header />
        <h2>Step 3: Questions About Your Symptoms</h2>
        {errorBanner && <div style={styles.banner}>{errorBanner}</div>}
        <form noValidate onSubmit={(e) => e.preventDefault()}>
          {triageQuestions.map((q, idx) => (
            <div style={styles.field} key={idx}>
              <label style={styles.label}>{q.question}</label>
              <select
                style={styles.input}
                value={q.answer ?? ''}
                onChange={(e) => {
                  const val = e.target.value;
                  setAnalysis((prev) => {
                    if (!prev) return prev;
                    const questions = [...(prev.triage_questions || [])];
                    questions[idx] = { ...questions[idx], answer: val };
                    return { ...prev, triage_questions: questions };
                  });
                }}
              >
                {(q.choices || []).map((opt) => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
              </select>
            </div>
          ))}
          <button type="button" style={styles.button} onClick={nextStep}>
            Continue →
          </button>
          <div style={{ height: 100 }} />
        </form>
      </div>
    );
  }

  // --- step 4: consent & disclosure ---
  if (step === 4) {
    return (
      <div style={styles.page}>
        {loading && <LoadingOverlay message={loadingMessage} />}
        <Header />
        <h2>Step 4: Patient Consent and Disclosure</h2>
        <h4>Click the following links to review documents</h4>
        {errorBanner && <div style={styles.banner}>{errorBanner}</div>}
        <form noValidate onSubmit={(e) => e.preventDefault()}>
          {packetDocs.map((doc, idx) => (
            <div key={idx}>
              <a href={doc.url} target="_blank" rel="noreferrer">
                {doc.name}
              </a>
            </div>
          ))}
          <p>Click the consent checkbox and type your name to confirm that you agree to above documents.</p>
          <label style={{ display: 'block', marginBottom: '0.5em' }}>
            <input
              type="checkbox"
              checked={!!analysis?.consent_confirmation}
              onChange={(e) => setField('consent_confirmation', e.target.checked)}
            />{' '}
            I consent to the statements in these documents
          </label>
          <div style={styles.field}>
            <input
              type="text"
              placeholder="Sign Here"
              disabled={!analysis?.consent_confirmation}
              value={(analysis?.consent_signature ?? '') as string}
              onChange={(e) => setField('consent_signature', e.target.value)}
              style={styles.input}
            />
          </div>
          <button type="button" style={styles.button} onClick={finishRegistration}>
            Finish Registration
          </button>
          <div style={{ height: 100 }} />
        </form>
      </div>
    );
  }

  // --- step 2: review & edit ---
  const patientIsMinor = isMinor(analysis?.patient_date_of_birth);

  return (
    <div style={styles.page}>
      {loading && <LoadingOverlay message={loadingMessage} />}
      <Header />
      <h2>Step 2: Review &amp; Edit Your Registration Details</h2>

      {errorBanner && <div style={styles.banner}>{errorBanner}</div>}

      {/* Validation progress indicator */}
      {validationSummary.requiredFieldCount > 0 && (
        <div style={styles.progressWrap}>
          <div>
            Registration Progress: {validationSummary.completedFieldCount} of{' '}
            {validationSummary.requiredFieldCount} required fields completed
          </div>
          <div style={styles.progressBar}>
            <div style={{ ...styles.progressFill, width: `${validationSummary.completionRate}%` }} />
          </div>
          {!validationSummary.valid && validationSummary.errors.length > 0 && (
            <div>
              <strong>Missing required fields:</strong>{' '}
              {validationSummary.errors.map((err, i) => (
                <span key={err.field}>
                  {getFieldDisplayName(err.field)}
                  {i < validationSummary.errors.length - 1 ? ', ' : ''}
                </span>
              ))}
            </div>
          )}
        </div>
      )}

      <form noValidate onSubmit={(e) => { e.preventDefault(); nextStep(); }}>
        <h3>Patient Information</h3>

        <div style={styles.field}>
          <label style={styles.label}>Patient Last Name</label>
          <input
            type="text"
            data-field="patient_last_name"
            {...textProps('patient_last_name')}
            onBlur={() => runFieldValidation('patient_last_name')}
          />
          {renderRequiredError('patient_last_name')}
        </div>

        <div style={styles.field}>
          <label style={styles.label}>Patient First Name</label>
          <input
            type="text"
            data-field="patient_first_name"
            {...textProps('patient_first_name')}
            onBlur={() => runFieldValidation('patient_first_name')}
          />
          {renderRequiredError('patient_first_name')}
        </div>

        <div style={styles.field}>
          <label style={styles.label}>Patient Middle Name</label>
          <input type="text" {...textProps('patient_middle_name')} />
        </div>

        <div style={styles.field}>
          <label style={styles.label}>Date of Birth</label>
          <input
            type="date"
            data-field="patient_date_of_birth"
            value={toDateInputValue(analysis?.patient_date_of_birth)}
            onChange={(e) => setField('patient_date_of_birth', e.target.value)}
            onBlur={() => runFieldValidation('patient_date_of_birth')}
            style={styles.input}
          />
          {renderRequiredError('patient_date_of_birth')}
          {patientIsMinor && (
            <div style={styles.minorIndicator}>
              Patient is under 18. Guarantor information will be required for financial responsibility.
            </div>
          )}
        </div>

        <div style={styles.field}>
          <label style={styles.label}>Patient Gender</label>
          <select
            data-field="patient_gender"
            {...selectProps('patient_gender')}
            onBlur={() => runFieldValidation('patient_gender')}
          >
            <option value="">Please select...</option>
            {GENDER_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
          {renderRequiredError('patient_gender')}
        </div>

        <div style={styles.field}>
          <label style={styles.label}>Patient Gender Identity</label>
          <select data-field="patient_gender_identity" {...selectProps('patient_gender_identity')}>
            <option value="ASKU"></option>
            {GENDER_IDENTITY_OPTIONS.map((o) => (
              <option key={o.label} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
          {renderRequiredError('patient_gender_identity')}
        </div>

        <div style={styles.field}>
          <label style={styles.label}>Patient Sexual Orientation</label>
          <select data-field="patient_sexual_orientation" {...selectProps('patient_sexual_orientation')}>
            <option value="ASKU"></option>
            {SEXUAL_ORIENTATION_OPTIONS.map((o) => (
              <option key={o.label} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
          {renderRequiredError('patient_sexual_orientation')}
        </div>

        {/* Legacy field name is misspelled `patient_martial_status`; preserved. */}
        <div style={styles.field}>
          <label style={styles.label}>Marital Status</label>
          <select data-field="patient_martial_status" {...selectProps('patient_martial_status')}>
            <option value="U"></option>
            {MARITAL_STATUS_OPTIONS.map((o) => (
              <option key={o.label} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
          {renderRequiredError('patient_martial_status')}
        </div>

        <div style={styles.field}>
          <label style={styles.label}>Race</label>
          <select data-field="patient_race" {...selectProps('patient_race')}>
            <option value="ASKU"></option>
            {RACE_OPTIONS.map((o) => (
              <option key={o.label} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
          {renderRequiredError('patient_race')}
        </div>

        <div style={styles.field}>
          <label style={styles.label}>Ethnicity</label>
          <select data-field="ethnicity" {...selectProps('ethnicity')}>
            <option value="other"></option>
            {ETHNICITY_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
          {renderRequiredError('ethnicity')}
        </div>

        <div style={styles.field}>
          <label style={styles.label}>Religion</label>
          <select data-field="religion" {...selectProps('religion')}>
            <option value="ASKU"></option>
            {RELIGION_OPTIONS.map((o) => (
              <option key={o.label} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
          {renderRequiredError('religion')}
        </div>

        <div style={styles.field}>
          <label style={styles.label}>Preferred Language</label>
          <select data-field="preferred_language" {...selectProps('preferred_language')}>
            <option value=""></option>
            {LANGUAGE_OPTIONS.filter((o) => o.value !== '').map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
            <option value="">Declined to specify</option>
          </select>
          {renderRequiredError('preferred_language')}
        </div>

        <div style={styles.field}>
          <label style={styles.label}>Smoking Status</label>
          <select data-field="patient_smoking_status" {...selectProps('patient_smoking_status')}>
            <option value="266927001"></option>
            {SMOKING_STATUS_OPTIONS.map((o) => (
              <option key={o.label} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
          {renderRequiredError('patient_smoking_status')}
        </div>

        <h4>Address Information</h4>
        <div style={styles.field}>
          <label style={styles.label}>Home Address Line 1</label>
          <input
            type="text"
            data-field="patient_home_address_line_1"
            {...textProps('patient_home_address_line_1')}
            onBlur={() => runFieldValidation('patient_home_address_line_1')}
          />
          {renderRequiredError('patient_home_address_line_1')}
        </div>

        <div style={styles.field}>
          <label style={styles.label}>Home Address Line 2</label>
          <input type="text" {...textProps('patient_home_address_line_2')} />
        </div>

        <div style={styles.field}>
          <label style={styles.label}>City</label>
          <input
            type="text"
            data-field="patient_home_address_city"
            {...textProps('patient_home_address_city')}
            onBlur={() => runFieldValidation('patient_home_address_city')}
          />
          {renderRequiredError('patient_home_address_city')}
        </div>

        <div style={styles.field}>
          <label style={styles.label}>Home Address State</label>
          <select
            data-field="patient_home_address_state"
            {...selectProps('patient_home_address_state')}
            onBlur={() => runFieldValidation('patient_home_address_state')}
          >
            <option disabled value="">
              Select state
            </option>
            {US_STATES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
          {renderRequiredError('patient_home_address_state')}
        </div>

        <div style={styles.field}>
          <label style={styles.label}>ZIP</label>
          <input
            type="text"
            data-field="patient_home_address_zip"
            {...textProps('patient_home_address_zip')}
            onBlur={() => runFieldValidation('patient_home_address_zip')}
          />
          {renderRequiredError('patient_home_address_zip')}
        </div>

        <h4>Contact Information</h4>
        <div style={styles.field}>
          <label style={styles.label}>Phone</label>
          <input
            type="text"
            data-field="patient_phone"
            placeholder="999-999-9999"
            {...textProps('patient_phone')}
            onBlur={() => runFieldValidation('patient_phone')}
          />
          {renderRequiredError('patient_phone')}
        </div>

        <div style={styles.field}>
          <label style={styles.label}>Email</label>
          <input type="text" {...textProps('patient_email')} />
        </div>

        <div style={styles.field}>
          <label style={styles.label}>Employer Name</label>
          <input type="text" {...textProps('patient_employer_name')} />
        </div>

        <div style={styles.field}>
          <label style={styles.label}>Emergency Contact Name</label>
          <input type="text" {...textProps('emergency_contact_name')} />
        </div>

        <div style={styles.field}>
          <label style={styles.label}>Emergency Contact Phone</label>
          <input type="text" placeholder="999-999-9999" {...textProps('emergency_contact_phone_mobile')} />
        </div>

        <div style={styles.field}>
          <label style={styles.label}>Patient SSN</label>
          <input type="text" placeholder="999-99-9999" {...textProps('patient_ssn')} />
        </div>

        <div style={styles.field}>
          <label style={styles.label}>License Number</label>
          <input type="text" {...textProps('drivers_license_number')} />
        </div>

        <div style={styles.field}>
          <label style={styles.label}>License State</label>
          <input type="text" {...textProps('drivers_license_state')} />
        </div>

        <h3>Guarantor/Responsible Party</h3>
        <div style={styles.field}>
          <label style={styles.label}>Patient Relationship to Guarantor</label>
          <select {...selectProps('guarantor_patient_relationship')}>
            <option value="UNK">Select relationship...</option>
            {GUARANTOR_RELATIONSHIP_OPTIONS.map((o) => (
              <option key={o.label} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>

        {/* Conditional guarantor fields - shown when needed for minors or
            non-self relationships (mirrors ng-show="isFieldRequired(...)"). */}
        {isFieldRequired('guarantor_first_name', analysis || {}) && (
          <>
            <div style={styles.field}>
              <label style={styles.label}>Guarantor First Name</label>
              <input
                type="text"
                data-field="guarantor_first_name"
                {...textProps('guarantor_first_name')}
                onBlur={() => runFieldValidation('guarantor_first_name')}
              />
              {renderRequiredError('guarantor_first_name')}
            </div>

            <div style={styles.field}>
              <label style={styles.label}>Guarantor Middle Name</label>
              <input type="text" {...textProps('guarantor_middle_name')} />
            </div>

            <div style={styles.field}>
              <label style={styles.label}>Guarantor Last Name</label>
              <input
                type="text"
                data-field="guarantor_last_name"
                {...textProps('guarantor_last_name')}
                onBlur={() => runFieldValidation('guarantor_last_name')}
              />
              {renderRequiredError('guarantor_last_name')}
            </div>
          </>
        )}

        <div style={styles.field}>
          <label style={styles.label}>Guarantor Date of Birth</label>
          <input
            type="date"
            value={toDateInputValue(analysis?.guarantor_date_of_birth)}
            onChange={(e) => setField('guarantor_date_of_birth', e.target.value)}
            style={styles.input}
          />
        </div>

        {/* Bug fix vs. legacy: the AngularJS <select> bound to the misspelled
            `guarantor_gener`, so a manually chosen guarantor gender never reached
            the backend (which reads `guarantor_gender`, views.py:650). Bind the
            field the backend actually reads — consistent with the Self copier. */}
        <div style={styles.field}>
          <label style={styles.label}>Guarantor Gender</label>
          <select {...selectProps('guarantor_gender')}>
            <option value="UNK"></option>
            {GENDER_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>

        <div style={styles.field}>
          <label style={styles.label}>Guarantor Address Line 1</label>
          <input
            type="text"
            data-field="guarantor_address_line_1"
            {...textProps('guarantor_address_line_1')}
            onBlur={() => runFieldValidation('guarantor_address_line_1')}
          />
          {renderRequiredError('guarantor_address_line_1')}
        </div>

        <div style={styles.field}>
          <label style={styles.label}>Guarantor Address Line 2</label>
          <input type="text" {...textProps('guarantor_address_line_2')} />
        </div>

        <div style={styles.field}>
          <label style={styles.label}>Guarantor Address City</label>
          <input type="text" {...textProps('guarantor_address_city')} />
        </div>

        <div style={styles.field}>
          <label style={styles.label}>Guarantor Address State</label>
          <select {...selectProps('guarantor_address_state')}>
            <option disabled value="">
              Select state
            </option>
            {US_STATES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </div>

        <div style={styles.field}>
          <label style={styles.label}>Guarantor Address ZIP</label>
          <input type="text" {...textProps('guarantor_address_zip')} />
        </div>

        <div style={styles.field}>
          <label style={styles.label}>Guarantor Phone</label>
          <input
            type="text"
            data-field="guarantor_phone"
            placeholder="999-999-9999"
            {...textProps('guarantor_phone')}
            onBlur={() => runFieldValidation('guarantor_phone')}
          />
          {renderRequiredError('guarantor_phone')}
        </div>

        <div style={styles.field}>
          <label style={styles.label}>Guarantor Employer Name</label>
          <input type="text" {...textProps('guarantor_employer_name')} />
        </div>

        <div style={styles.field}>
          <label style={styles.label}>Guarantor SSN</label>
          <input type="text" placeholder="999-99-9999" {...textProps('guarantor_ssn')} />
        </div>

        <h3>Insurance</h3>
        <label style={{ display: 'block', marginBottom: '0.5em' }}>
          <input
            type="checkbox"
            checked={!!analysis?.no_insurance}
            onChange={(e) => setField('no_insurance', e.target.checked)}
          />{' '}
          Self-Pay/No Insurance
        </label>

        <div style={styles.field}>
          <label style={styles.label}>Insurance Company</label>
          <input
            type="text"
            data-field="insurance_company"
            {...textProps('insurance_company')}
            disabled={!!analysis?.no_insurance}
            onBlur={() => runFieldValidation('insurance_company')}
          />
          {renderRequiredError('insurance_company')}
        </div>

        <div style={styles.field}>
          <label style={styles.label}>Insurance Company Address Line 1</label>
          <input
            type="text"
            {...textProps('insurance_company_address_line_1')}
            disabled={!!analysis?.no_insurance}
          />
        </div>

        <div style={styles.field}>
          <label style={styles.label}>Insurance Company Address Line 2</label>
          <input
            type="text"
            {...textProps('insurance_company_address_line_2')}
            disabled={!!analysis?.no_insurance}
          />
        </div>

        <div style={styles.field}>
          <label style={styles.label}>Insurance Company Address City</label>
          <input
            type="text"
            {...textProps('insurance_company_address_city')}
            disabled={!!analysis?.no_insurance}
          />
        </div>

        <div style={styles.field}>
          <label style={styles.label}>Insurance Company Address State</label>
          <select {...selectProps('insurance_company_address_state')} disabled={!!analysis?.no_insurance}>
            <option disabled value="">
              Select state
            </option>
            {US_STATES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </div>

        <div style={styles.field}>
          <label style={styles.label}>Insurance Company Address ZIP</label>
          <input
            type="text"
            {...textProps('insurance_company_address_zip')}
            disabled={!!analysis?.no_insurance}
          />
        </div>

        <div style={styles.field}>
          <label style={styles.label}>Policy Number</label>
          <input
            type="text"
            data-field="insurance_policy_number"
            {...textProps('insurance_policy_number')}
            disabled={!!analysis?.no_insurance}
            onBlur={() => runFieldValidation('insurance_policy_number')}
          />
          {renderRequiredError('insurance_policy_number')}
        </div>

        <div style={styles.field}>
          <label style={styles.label}>Patient Relationship to Holder</label>
          <select {...selectProps('insurance_patient_relationship')} disabled={!!analysis?.no_insurance}>
            <option value="UNK">Select relationship</option>
            {INSURANCE_RELATIONSHIP_OPTIONS.map((o) => (
              <option key={o.label} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>

        <div style={styles.field}>
          <label style={styles.label}>Holder Name</label>
          <input
            type="text"
            data-field="insurance_holder_name"
            {...textProps('insurance_holder_name')}
            disabled={!!analysis?.no_insurance}
            onBlur={() => runFieldValidation('insurance_holder_name')}
          />
          {renderRequiredError('insurance_holder_name')}
        </div>

        <div style={styles.field}>
          <label style={styles.label}>Holder Gender</label>
          <select {...selectProps('insurance_holder_gender')} disabled={!!analysis?.no_insurance}>
            <option value="UNK">Select gender</option>
            {GENDER_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>

        <div style={styles.field}>
          <label style={styles.label}>Holder DOB</label>
          <input
            type="date"
            data-field="insurance_holder_dob"
            value={toDateInputValue(analysis?.insurance_holder_dob)}
            onChange={(e) => setField('insurance_holder_dob', e.target.value)}
            disabled={!!analysis?.no_insurance}
            onBlur={() => runFieldValidation('insurance_holder_dob')}
            style={styles.input}
          />
          {renderRequiredError('insurance_holder_dob')}
        </div>

        <div style={styles.field}>
          <label style={styles.label}>Holder Phone</label>
          <input
            type="text"
            placeholder="999-999-9999"
            {...textProps('insurance_holder_phone')}
            disabled={!!analysis?.no_insurance}
          />
        </div>

        <button type="submit" style={styles.button}>
          Continue →
        </button>
        <div style={{ height: 100 }} />
      </form>
    </div>
  );
};

export default SelfRegistrationPage;
